/**
 * Shared domain constants: platforms, statuses, limits, pricing tiers.
 * Imported by both the API (validation + publishing rules) and the web app (UI labels).
 */
export const PLATFORM_IDS = [
    'instagram',
    'facebook',
    'tiktok',
    'linkedin',
    'twitter',
    'youtube',
];
export const PLATFORMS = {
    instagram: {
        id: 'instagram',
        label: 'Instagram',
        maxChars: 2200,
        recommendedChars: 125,
        maxHashtags: 30,
        supportsImages: true,
        supportsVideo: true,
        requiresMedia: true,
        color: '#E1306C',
    },
    facebook: {
        id: 'facebook',
        label: 'Facebook',
        maxChars: 63206,
        recommendedChars: 400,
        maxHashtags: 10,
        supportsImages: true,
        supportsVideo: true,
        requiresMedia: false,
        color: '#1877F2',
    },
    tiktok: {
        id: 'tiktok',
        label: 'TikTok',
        maxChars: 2200,
        recommendedChars: 150,
        maxHashtags: 12,
        supportsImages: false,
        supportsVideo: true,
        requiresMedia: true,
        color: '#010101',
    },
    linkedin: {
        id: 'linkedin',
        label: 'LinkedIn',
        maxChars: 3000,
        recommendedChars: 700,
        maxHashtags: 5,
        supportsImages: true,
        supportsVideo: true,
        requiresMedia: false,
        color: '#0A66C2',
    },
    twitter: {
        id: 'twitter',
        label: 'X (Twitter)',
        maxChars: 280,
        recommendedChars: 240,
        maxHashtags: 3,
        supportsImages: true,
        supportsVideo: true,
        requiresMedia: false,
        color: '#0F1419',
    },
    youtube: {
        id: 'youtube',
        label: 'YouTube',
        /** The video *description* limit. The title has its own, much smaller cap. */
        maxChars: 5000,
        recommendedChars: 1000,
        /** YouTube ignores the 16th hashtag onwards, so there is no point sending more. */
        maxHashtags: 15,
        // A YouTube upload is the video file itself: a still image cannot be posted,
        // and every upload needs a clip, so both gates are on.
        supportsImages: false,
        supportsVideo: true,
        requiresMedia: true,
        color: '#FF0000',
    },
};
export const POST_STATUSES = [
    'queued',
    'preparing',
    'approving',
    'scheduled',
    'posting',
    'posted',
    'failed',
    'cancelled',
];
export const CAMPAIGN_STATUSES = [
    'draft',
    'planning',
    'active',
    'paused',
    'completed',
    'failed',
];
/**
 * Tone vocabulary shared by the web composer, the Node fallback generator and
 * the Python worker (`services/ai-worker/app/schemas.py` exposes the same list).
 * Keep the three in sync — a tone offered by the UI that is missing here is
 * rejected by `aiGenerateSchema` before it ever reaches the worker.
 */
export const TONES = [
    'friendly',
    'professional',
    'casual',
    'humorous',
    'witty',
    'inspirational',
    'bold',
    'educational',
];
export const LENGTHS = ['short', 'medium', 'long'];
export const METRIC_KEYS = [
    'impressions',
    'reach',
    'engagements',
    'likes',
    'comments',
    'shares',
    'clicks',
    'saves',
    'video_views',
];
// ---------------------------------------------------------------------------
// Media library — AI generated images and videos that can be attached to posts.
// ---------------------------------------------------------------------------
export const MEDIA_KINDS = ['image', 'video'];
/** `queued`/`processing` are used by async video jobs; images resolve immediately. */
export const MEDIA_STATUSES = ['queued', 'processing', 'ready', 'failed'];
export const MEDIA_ASPECT_RATIOS = ['square', 'portrait', 'story', 'landscape'];
export const MEDIA_ASPECT_RATIO_MAP = {
    square: { id: 'square', label: '1:1', width: 1024, height: 1024, videoFriendly: false },
    portrait: { id: 'portrait', label: '4:5', width: 896, height: 1152, videoFriendly: true },
    story: { id: 'story', label: '9:16', width: 704, height: 1280, videoFriendly: true },
    landscape: { id: 'landscape', label: '16:9', width: 1280, height: 704, videoFriendly: true },
};
export const MEDIA_DEFAULT_ASPECT_RATIO = 'square';
export function aspectRatioDimensions(id) {
    const descriptor = MEDIA_ASPECT_RATIO_MAP[id] ?? MEDIA_ASPECT_RATIO_MAP[MEDIA_DEFAULT_ASPECT_RATIO];
    return { width: descriptor.width, height: descriptor.height };
}
/** Video presets offered in the composer (short clips keep GPU jobs queue-friendly). */
export const VIDEO_DURATIONS = [3, 5, 8];
export const VIDEO_DEFAULT_DURATION_SECONDS = 5;
export const VIDEO_DEFAULT_FPS = 24;
export const MEDIA_LIMITS = {
    maxImagePromptChars: 600,
    maxVideoPromptChars: 5000,
    maxNegativePromptChars: 400,
    maxMediaPerPost: 10,
    maxAssetsPerRequest: 4,
    maxImageWidth: 1536,
    maxImageHeight: 1536,
    maxVideoWidth: 1280,
    maxVideoHeight: 1280,
    minVideoDurationSeconds: 1,
    maxVideoDurationSeconds: 10,
};
/** Placeholder artwork host used while the GPU worker is not reachable. */
export const PLACEHOLDER_IMAGE_BASE = 'https://placehold.co';
/** The URL that should end up in `posts.media_urls` for a given asset. */
export function preferredMediaUrl(asset) {
    return asset.url ?? asset.thumbnail_url ?? null;
}
const IMAGE_EXTENSIONS = /\.(png|jpe?g|webp|gif|bmp|heic|heif)$/i;
const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|avi|mkv)$/i;
/**
 * Best-effort media kind from a bare URL.
 *
 * `posts.media_urls` is a plain `TEXT[]`, so by the time a publisher runs the
 * only signal left is the file extension. Instagram needs this to pick between
 * an `image_url` container and a `REELS` one; YouTube rejects anything that is
 * not a clip. Unknown extensions fall back to `image`, which is the safer of the
 * two: an image container fails fast, while guessing "video" makes the provider
 * spend minutes transcoding before it rejects the file.
 */
export function inferMediaKind(url) {
    const clean = url.split(/[?#]/)[0] ?? url;
    if (VIDEO_EXTENSIONS.test(clean))
        return 'video';
    if (IMAGE_EXTENSIONS.test(clean))
        return 'image';
    return 'image';
}
export function platformSupportsMedia(platform, kind) {
    const descriptor = PLATFORMS[platform];
    if (!descriptor)
        return false;
    return kind === 'video' ? descriptor.supportsVideo : descriptor.supportsImages;
}
/** Platforms that would reject the given media kind — surfaced as a UI warning. */
export function platformsRejectingMedia(platforms, kind) {
    return platforms.filter((platform) => !platformSupportsMedia(platform, kind));
}
export const PLAN_LIMITS = {
    free: { postsPerMonth: 30, accounts: 2, campaignsPerMonth: 2, aiCreditsPerMonth: 100 },
    creator: { postsPerMonth: 500, accounts: 8, campaignsPerMonth: 25, aiCreditsPerMonth: 3000 },
    agency: { postsPerMonth: 5000, accounts: 40, campaignsPerMonth: 250, aiCreditsPerMonth: 25000 },
};
// ---------------------------------------------------------------------------
// Billing — credit-based plans, UPI + card checkout, referral rewards
//
// IMPORTANT: the product is denominated in CREDITS, not money. A customer pays
// via UPI/card and that payment is CONVERTED INTO credits at the point of
// purchase. Credits are a promotional balance only: they have no monetary value,
// are not redeemable for cash, cannot be transferred, and cannot be withdrawn.
// See the Privacy Policy and Terms & Conditions for the full statement.
// ---------------------------------------------------------------------------
/** Razorpay price fields are in the smallest currency unit (paise), never floats. */
export const BILLING_CURRENCY = 'INR';
/** Name of the balance unit shown everywhere in the UI and legal documents. */
export const CREDIT_UNIT = 'credit';
export const PAYMENT_METHODS = ['upi', 'card'];
export const PAYMENT_METHOD_LABELS = {
    upi: 'UPI',
    card: 'Card',
};
/**
 * The paid catalogue. `credits` is what the customer receives; `pricePaise` is
 * what they pay. `PLAN_LIMITS` above stays the source of truth for what each
 * plan may do.
 */
export const PLANS = {
    free: {
        id: 'free',
        label: 'Free',
        credits: 0,
        pricePaise: 0,
        tagline: 'Try the autopilot on one account',
        highlights: ['30 posts / month', '2 connected accounts', '100 credits to start'],
    },
    creator: {
        id: 'creator',
        label: 'Creator',
        credits: 1_000,
        pricePaise: 99_900,
        tagline: 'For a solo creator publishing daily',
        highlights: ['500 posts / month', '8 connected accounts', '1,000 credits included'],
        popular: true,
    },
    agency: {
        id: 'agency',
        label: 'Agency',
        credits: 6_000,
        pricePaise: 499_900,
        tagline: 'For teams running many client brands',
        highlights: ['5,000 posts / month', '40 connected accounts', '6,000 credits included'],
    },
};
export const PAID_PLAN_IDS = ['creator', 'agency'];
export const SUBSCRIPTION_STATUSES = [
    'inactive',
    'active',
    'past_due',
    'cancelled',
];
export const PAYMENT_STATUSES = [
    'created',
    'authorized',
    'captured',
    'failed',
    'refunded',
];
/** Why a credit ledger row exists. Purely descriptive; balance = SUM(amount). */
export const CREDIT_REASONS = [
    'purchase', // credits granted by a completed payment
    'spend', // credits consumed to reduce a payment
    'referral_reward', // credited to the referrer when their invite converts
    'referral_welcome', // credited to the new account when its first payment lands
    'adjustment', // manual support/admin correction
];
export const REFERRAL_STATUSES = ['pending', 'qualified', 'rewarded', 'rejected'];
/**
 * Referral programme.
 *
 * Rewards are paid in CREDITS, which are not money and cannot be withdrawn. A
 * referral only converts once the invited account makes its first captured
 * payment, which is what stops a farm of empty sign-ups from collecting credit.
 * `maxCreditsPerPurchase` caps how much credit one purchase may absorb.
 */
export const REFERRAL_PROGRAM = {
    /** Credited to the referrer when their invite converts. */
    referrerRewardCredits: 250,
    /** Credited to the new account when its first payment succeeds. */
    refereeRewardCredits: 100,
    /** Ceiling on how much credit a single purchase may absorb. */
    maxCreditsPerPurchase: 250,
    /** A referral converts on the referred account's Nth captured payment. */
    qualifiedAfterPayments: 1,
    codePrefix: 'AIM',
};
export const JOB_QUEUES = ['ai-content', 'publish', 'analytics', 'insights', 'media'];
export const API_ROUTES = {
    register: '/auth/register',
    login: '/auth/login',
    refresh: '/auth/refresh',
    me: '/auth/me',
    accounts: '/accounts',
    connect: (platform) => `/social/connect/${platform}`,
    posts: '/content/posts',
    post: (id) => `/content/posts/${id}`,
    approvePost: (id) => `/content/posts/${id}/approve`,
    publishPost: (id) => `/content/posts/${id}/publish`,
    cancelPost: (id) => `/content/posts/${id}/cancel`,
    campaigns: '/campaigns',
    campaign: (id) => `/campaigns/${id}`,
    campaignPosts: (id) => `/campaigns/${id}/posts`,
    campaignPublish: (id) => `/campaigns/${id}/publish`,
    campaignInsights: '/campaigns/insights',
    media: '/media/assets',
    mediaAsset: (id) => `/media/assets/${id}`,
    mediaImages: '/media/images',
    mediaVideos: '/media/videos',
    mediaAttach: (id) => `/media/assets/${id}/attach`,
    mediaDetach: (id, postId) => `/media/assets/${id}/attach/${postId}`,
    mediaHealth: '/media/health',
    dashboard: '/analytics/dashboard',
    postMetrics: (id) => `/analytics/posts/${id}`,
    exportAnalytics: '/analytics/export',
    billing: '/billing',
    billingPlans: '/billing/plans',
    billingCheckout: '/billing/checkout',
    billingVerify: (id) => `/billing/checkout/${id}/verify`,
    billingReferral: '/billing/referral',
    billingApplyReferral: '/billing/referral/apply',
};
